import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { useModal } from "../../lib/ModalContext";
import { submitLead } from "../../lib/api";
import { EASE } from "../../lib/motion";
import { executeCaptcha } from "../../lib/captcha";
import { trackEvent } from "../../lib/analytics";
import { PortalLoginModal } from "./PortalLoginModal";

const Shell = ({ children, title, eyebrow, testId, size = "default" }) => {
    const { close } = useModal();
    const widthClass =
        size === "wide"
            ? "max-w-[920px]"
            : "max-w-[560px]";
    return (
        <AnimatePresence>
            <motion.div
                key="backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="fixed inset-0 z-[80] flex items-center justify-center p-4 md:p-8"
                onClick={close}
                style={{ background: "rgba(10,10,10,0.45)", backdropFilter: "blur(8px)" }}
                data-testid={`modal-backdrop-${testId}`}
            >
                <motion.div
                    key="modal"
                    initial={{ opacity: 0, scale: 0.96, y: 16 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.97, y: 10 }}
                    transition={{ duration: 0.7, ease: EASE }}
                    onClick={(e) => e.stopPropagation()}
                    className={`glass-modal w-full ${widthClass} max-h-[92vh] overflow-hidden relative flex flex-col`}
                    data-testid={`modal-${testId}`}
                >
                    <button
                        onClick={close}
                        aria-label="Close"
                        className="absolute top-5 right-5 z-20 w-9 h-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center transition-colors shadow"
                        data-testid={`modal-close-${testId}`}
                    >
                        <X size={16} />
                    </button>
                    <div data-lenis-prevent className={`overflow-y-auto thin-scroll flex-1 ${size === "wide" ? "p-6 md:p-8" : "p-8 md:p-10"}`}>
                        {eyebrow && <div className="text-xs uppercase tracking-[0.22em] text-oth-ink3 font-body">{eyebrow}</div>}
                        {title && <div className="mt-2 font-display text-3xl md:text-4xl font-semibold tracking-tight">{title}</div>}
                        <div className={title || eyebrow ? "mt-6" : ""}>{children}</div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

const ContactModal = () => {
    const { close } = useModal();
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [phoneError, setPhoneError] = useState("");

    // Accepts only digits — strips anything else as the user types so validation
    // is unambiguous. Allows up to 10 digits.
    const onPhoneChange = (raw) => {
        const digits = (raw || "").replace(/\D/g, "").slice(0, 10);
        setForm((f) => ({ ...f, phone: digits }));
        if (digits.length === 0 || digits.length === 10) setPhoneError("");
        else setPhoneError("Please enter exactly 10 digits.");
    };

    const submit = async (e) => {
        e.preventDefault();
        if (form.phone.length !== 10) {
            setPhoneError("Please enter exactly 10 digits.");
            return;
        }
        setSubmitting(true);
        try {
            const captcha_token = await executeCaptcha("contact");
            await submitLead({ ...form, type: "contact", captcha_token });
            trackEvent("contact_submit", { form: "contact_us" });
            toast.success("Thank you — we'll be in touch within one business day.");
            close();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (typeof detail === "string") {
                toast.error(detail);
            } else {
                toast.success("Thank you — we'll be in touch within one business day.");
                close();
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Shell title="Contact Us" eyebrow="Start a quiet conversation" testId="contact">
            <form onSubmit={submit} className="space-y-2" data-testid="contact-form">
                <input className="field" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="contact-input-name" />
                <input type="email" className="field" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="contact-input-email" />
                <input
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    pattern="[0-9]{10}"
                    maxLength={10}
                    className="field"
                    placeholder="Mobile number (10 digits)"
                    value={form.phone}
                    onChange={(e) => onPhoneChange(e.target.value)}
                    required
                    data-testid="contact-input-phone"
                    aria-invalid={Boolean(phoneError)}
                />
                {phoneError && (
                    <p className="text-xs text-red-600 pl-1 font-body" data-testid="contact-phone-error">{phoneError}</p>
                )}
                <textarea className="field min-h-[100px] resize-none" placeholder="What would be most helpful to talk about?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-input-message" />
                <button type="submit" disabled={submitting || form.phone.length !== 10} className="btn-pill btn-pill-dark mt-8 w-full" data-testid="contact-submit">
                    {submitting ? "Sending..." : "Send message"} <ArrowUpRight size={14} />
                </button>
                <p className="text-[10px] text-oth-ink3 font-body leading-relaxed mt-3 text-center opacity-70" data-testid="contact-recaptcha-attribution">
                    Protected by reCAPTCHA — Google{" "}
                    <a className="underline" href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Privacy</a>
                    {" · "}
                    <a className="underline" href="https://policies.google.com/terms" target="_blank" rel="noreferrer">Terms</a>
                </p>
            </form>
        </Shell>
    );
};

const LoginModal = () => {
    const { close, open } = useModal();
    const [form, setForm] = useState({ email: "", password: "" });
    const submit = (e) => {
        e.preventDefault();
        toast.success("Welcome back. (Family portal coming soon.)");
        close();
    };
    return (
        <Shell title="Welcome back" eyebrow="Family member sign-in" testId="login">
            <form onSubmit={submit} className="space-y-2" data-testid="login-form">
                <input type="email" className="field" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="login-input-email" />
                <input type="password" className="field" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="login-input-password" />
                <button type="submit" className="btn-pill btn-pill-dark mt-8 w-full" data-testid="login-submit">Sign in</button>
                <div className="mt-6 text-center text-sm text-oth-ink3 font-body">
                    Are you our admin?{" "}
                    <a href="/adminoth" className="underline hover:text-oth-blue transition-colors" data-testid="login-admin-link">Admin portal</a>
                </div>
            </form>
        </Shell>
    );
};

const FAQModal = ({ content }) => {
    const items = content?.faq?.items || [];
    const [openIdx, setOpenIdx] = useState(0);
    return (
        <Shell title="Frequently asked" eyebrow={content?.faq?.eyebrow} testId="faq">
            <div className="divide-y divide-oth-line/70" data-testid="faq-list">
                {items.map((it, i) => {
                    const open = openIdx === i;
                    return (
                        <button key={`m-faq-${it.q || i}`} onClick={() => setOpenIdx(open ? -1 : i)} className="w-full text-left py-5" data-testid={`faq-item-${i}`}>
                            <div className="flex items-center justify-between gap-4">
                                <span className="font-display text-lg md:text-xl font-semibold tracking-tight">{it.q}</span>
                                <span className="w-8 h-8 rounded-full bg-white flex items-center justify-center shrink-0">
                                    <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.6, ease: EASE }}>
                                        {open ? <Minus size={14} /> : <Plus size={14} />}
                                    </motion.span>
                                </span>
                            </div>
                            <AnimatePresence initial={false}>
                                {open && (
                                    <motion.p
                                        initial={{ height: 0, opacity: 0, marginTop: 0 }}
                                        animate={{ height: "auto", opacity: 1, marginTop: 12 }}
                                        exit={{ height: 0, opacity: 0, marginTop: 0 }}
                                        transition={{ duration: 0.7, ease: EASE }}
                                        className="font-body text-base text-oth-ink2 leading-relaxed overflow-hidden"
                                    >
                                        {it.a}
                                    </motion.p>
                                )}
                            </AnimatePresence>
                        </button>
                    );
                })}
            </div>
        </Shell>
    );
};

const BookModal = ({ content }) => {
    const url = content?.brand?.calendly_url || "https://calendly.com";
    const widgetRef = useRef(null);

    useEffect(() => {
        // Load Calendly widget script once
        const existing = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
        if (!existing) {
            const s = document.createElement("script");
            s.src = "https://assets.calendly.com/assets/external/widget.js";
            s.async = true;
            document.body.appendChild(s);
        } else if (window.Calendly && widgetRef.current) {
            try {
                window.Calendly.initInlineWidget({ url, parentElement: widgetRef.current });
            } catch (e) {
                console.debug("Calendly init failed", e);
            }
        }
        trackEvent("book_modal_open", {});
    }, [url]);

    return (
        <Shell title="Book a private slot" eyebrow="A 30-minute conversation" testId="book" size="wide">
            <div
                ref={widgetRef}
                className="calendly-inline-widget rounded-2xl overflow-hidden"
                data-url={url}
                style={{ minWidth: "280px", height: "640px" }}
                data-testid="calendly-inline"
            />
        </Shell>
    );
};

const DisclaimerModal = ({ content }) => {
    const d = content?.footer?.disclaimer || {};
    const body = (typeof d.body === "string" && d.body.trim())
        ? d.body
        : "Add your disclaimer text from Admin → Footer → Disclaimer modal.";
    return (
        <Shell title={d.title || "Disclaimer"} eyebrow={d.eyebrow || "Legal"} testId="disclaimer">
            <div
                className="font-body text-sm text-oth-ink2 leading-relaxed whitespace-pre-wrap"
                data-testid="disclaimer-body"
            >
                {body}
            </div>
        </Shell>
    );
};

export const Modals = ({ content }) => {
    const { active, payload } = useModal();
    if (active === "contact") return <ContactModal />;
    if (active === "login") return <LoginModal />;
    if (active === "faq") return <FAQModal content={content} />;
    if (active === "disclaimer") return <DisclaimerModal content={content} />;
    if (active === "book") return <BookModal content={content} />;
    if (active === "portal-login") return <PortalLoginModal content={content} initialError={payload?.error} />;
    return null;
};
